import * as React from 'react'
import { createContext, useContext, useMemo, ReactNode } from 'react'
import { useAuth } from 'react-oidc-context'
import {
  useQueryAccount,
  useQueryRoles,
  useQueryEffectivePermissions,
} from '@/modules/shared/query-client'
import {
  AccountData,
  RolePermission,
  ServerAccessModel,
  effectiveRolesInCompany,
} from '@/modules/shared/types/account'
import { saveUserInfo } from 'app/services/local/roleService'
import { mapUserInfoFields } from 'app/actions/user'

interface UserContextValue {
  accountData: AccountData | undefined
  rolesData: RolePermission[] | undefined
  isLoading: boolean
  isAdmin(): boolean
  isPaymentAdmin(): boolean
  isCompanyAdmin(): boolean
  isDemoCompanyAdmin(): boolean
  getRolesInCompany(companyId: string): RolePermission[]
  getRoleNamesInCompany(companyId: string): string
  isDriver(companyId: string): boolean
  isManager(companyId: string): boolean
  hasServerAccess(): boolean
  getServerAccess(): ServerAccessModel[]
}

const UserContext = createContext<UserContextValue | null>(null)

export const useUser = () => {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUser must be used inside <UserProvider>')
  return ctx
}

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth()
  const sub = auth.user?.profile.sub ?? ''

  const { data: accountData, isLoading: accountLoading } = useQueryAccount({ userUniqueId: sub })
  const { data: rolesData, isLoading: rolesLoading } = useQueryRoles({ userUniqueId: sub })
  const { data: effectivePermissions, isLoading: permissionsLoading } =
    useQueryEffectivePermissions({ userUniqueId: sub })

  const isLoading = accountLoading || rolesLoading || permissionsLoading

  // Hydrate legacy roleService cache (used by class components / redux thunks
  // that still call isAdmin() / getUserInfo() synchronously). No localStorage —
  // refetched on every fresh tab via TanStack Query. Done in useMemo (not
  // useEffect) so the cache is warm on the same render the children consume.
  useMemo(() => {
    if (!accountData || !rolesData) return
    saveUserInfo(mapUserInfoFields(accountData), rolesData as any, [], effectivePermissions)
  }, [accountData, rolesData, effectivePermissions])

  const value = useMemo<UserContextValue>(() => {
    const isAdmin = () => accountData?.globalAdmin ?? false
    const isPaymentAdmin = () => accountData?.paymentAdmin ?? false
    const isCompanyAdmin = () => accountData?.companyAdmin ?? false
    const isDemoCompanyAdmin = () => accountData?.demoCompanyAdmin ?? false

    // Effective permissions for the company, from the server projection. See effectiveRolesInCompany.
    const getRolesInCompany = (companyId: string): RolePermission[] =>
      effectiveRolesInCompany(effectivePermissions, companyId)

    const getRoleNamesInCompany = (companyId: string): string => {
      if (!accountData) return ''
      const assignment = accountData.roles.find((r) => r.companyId == companyId)
      if (!assignment || assignment.roles.length === 0) return ''
      return assignment.roles.map((r) => r.name).join(', ')
    }

    const isDriver = (companyId: string): boolean => {
      if (isAdmin() || isPaymentAdmin() || isCompanyAdmin() || isDemoCompanyAdmin()) return false
      return getRolesInCompany(companyId).some((r) => r.isDriver)
    }

    const isManager = (companyId: string): boolean => {
      if (isAdmin() || isPaymentAdmin() || isCompanyAdmin() || isDemoCompanyAdmin()) return false
      return getRolesInCompany(companyId).some(
        (r) =>
          r.canEditCompany ||
          r.canCreateCompany ||
          r.canRemoveCompany ||
          r.canEditUser ||
          r.canCreateUser ||
          r.canRemoveUser,
      )
    }

    const getServerAccess = (): ServerAccessModel[] => accountData?.serverAccess ?? []
    const hasServerAccess = (): boolean => (accountData?.serverAccess?.length ?? 0) > 0

    return {
      accountData,
      rolesData,
      isLoading,
      isAdmin,
      isPaymentAdmin,
      isCompanyAdmin,
      isDemoCompanyAdmin,
      getRolesInCompany,
      getRoleNamesInCompany,
      isDriver,
      isManager,
      hasServerAccess,
      getServerAccess,
    }
  }, [accountData, rolesData, effectivePermissions, isLoading])

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
